/**
 * Utility that provides an easy usage for Treasury smartcontract.
 *
 * <i>Copyright (c) 2019 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module treasury.js
 * @class Treasury
 * @since 0.4.0
*/

const Tx = require('ethereumjs-tx').Transaction,
    Web3 = require('web3'),
    metamask = require('../helpers/metamask'),
    errorMessages = require('../constants/error-messages'),
    energiTx = require('../helpers/energi-tx'),
    uuidUtils = require('../helpers/uuid'),
    network = require('../constants/network-info'),
    HEX0 = '0x0';

const {toWei, toBN, toHex, isAddress} = Web3.utils;

const proposalFee = toHex(toWei('100', 'ether')),
    proposedAmountMin = toBN(toWei('100', 'ether')),
    proposedAmountMax = toBN(toWei('184000', 'ether')),
    proposalPeriodMin = 14*24*60*60,
    proposalPeriodMax = 30*24*60*60,
    proposalCallGas = '3000000';


let networkAddress = network.networkAddress,
    smartContractAddressTreasury = network.smartContractAddressTreasury,
    abiTreasury = network.abiTreasury,
    abiProposal = network.abiProposal,
    networkId = network.networkId,
    chainId = network.chainId,
    treasuryContract, web3;

/**
 * Creates a budget Proposal, owned by the person who is signing the transaction.
 *
 * @method budgetPropose
 * @param amount {String|BN} the amount of Wei's the proposer would like to receive
 * @param uuid {String} the address that holds the MNRG's
 * @param period {Number} the period that the purpose is valid
 * @param [privateKey] {String} only to be used by tests: the signer is the owner of the proposal
 * @return {Promise}
 * @since 0.4.0
 */
const budgetPropose = async (amount, uuid, period, privateKey) => {
    let txCount, transactionData, rawTx, tx, account, gasPrice,
        serializedTx, privateKeyBuffer, txResponse, energiCommon;

    // Check amount is between 100 and 184000 ether
    const amontBN = toBN(amount);
    if (proposedAmountMin.cmp(amontBN) === 1 || proposedAmountMax.cmp(amontBN) == -1) {
        throw new TreasuryException(errorMessages.INVALID_AMOUNT);
    }

    // Check period is between 2 weeks and 30 days
    if (period < proposalPeriodMin || period > proposalPeriodMax) {
        throw new TreasuryException(errorMessages.INVALID_PERIOD);
    }

    uuid = uuidUtils.uuidToHexBNString(uuid);

    transactionData = treasuryContract.methods.propose(amount, uuid, period).encodeABI();

    // If a private key is given, sign the transaction,
    // otherwise, return the rawTx so that MEW can process it:
    if (!privateKey) {
        // Create transaction object:
        rawTx = {
            gasLimit: proposalCallGas,
            value: '100',
            to: smartContractAddressTreasury,
            data: transactionData,
        };
        // we NEED an extra check, to be sure that rawTx.to is a valid ethereum address, otherwise the tx-funds will be lost:
        if (!rawTx.to || !isAddress(rawTx.to)) {
            throw new TreasuryException(errorMessages.INVALID_SMARTCONTRACT_ADDRESS);
        }

        // if the user connected to metamask, use metamask, otherwise return rawTx so that myEnergiWallet can process:
        if (await metamask.isConnected()) {
            rawTx.from = await metamask.getAddress();
            rawTx.gasLimit = web3.utils.toHex(proposalCallGas);
            rawTx.value = web3.utils.toHex(web3.utils.toWei('100', 'ether'));
            tx = await metamask.sendTx(rawTx);
            // Metamask transaction: return tx as a String:
            return tx;
        }

        rawTx.warnings = 0; // suppress warnings from EnergiWallet
        rawTx.ts = Date.now(); // forcing non cachable url
        // Non Metamask transaction: return rawTx as an Object:

        return rawTx;
    }

    gasPrice = await web3.eth.getGasPrice();
    account = web3.eth.accounts.privateKeyToAccount(privateKey);
    txCount = await web3.eth.getTransactionCount(account.address, 'pending');
    // Create transaction object:
    rawTx = {
        nonce: toHex(txCount),
        gasLimit: toHex(proposalCallGas),
        gasPrice: toHex(gasPrice),
        to: smartContractAddressTreasury,
        value: proposalFee,
        data: transactionData
    };

    energiCommon = await energiTx.getCommon(web3, networkAddress, networkId, chainId);
    tx = new Tx(rawTx, {common: energiCommon});

    privateKeyBuffer = Buffer.from(privateKey.substr(2), 'hex');
    tx.sign(privateKeyBuffer);

    // Broadcast the transaction to the testnet/mainnet
    serializedTx = tx.serialize();

    txResponse = await web3.eth.sendSignedTransaction('0x' + serializedTx.toString('hex'));

    if (!txResponse.status) {
        throw new TreasuryException(txResponse);
    }
    return txResponse;
};

/**
 * Lets us Masternode owner - the one who is going to sign - vote for a proposal.
 *
 * @method voteAccept
 * @param uuid {String} unique proposal uuid to vote for
 * @param [privateKey] {String} only to be used by tests: the signer is the owner of the proposal
 * @return {Promise}
 * @since 0.4.0
 */
const voteAccept = async (uuid, privateKey) => {
    let txCount, transactionData, rawTx, tx, account, gasPrice,
        serializedTx, privateKeyBuffer, txResponse, energiCommon,
        proposalContract, proposalAddress;

    if (!uuidUtils.isUuidv4(uuid, true)) { // second argument must be true: we also need to accept core node proposals which UUID's are less strict
        throw new TreasuryException(errorMessages.INVALID_UUID);
    }
    uuid = uuidUtils.uuidToHexBNString(uuid);

    proposalAddress = await treasuryContract.methods.uuid_proposal(uuid).call();
    if (!proposalAddress ||
        !web3.utils.isAddress(proposalAddress) ||
        (proposalAddress === '0x' + '0'.repeat(40))) {
        throw new TreasuryException(errorMessages.PROPOSAL_NOT_FOUND);
    }

    proposalContract = new web3.eth.Contract(abiProposal, proposalAddress);

    transactionData = await proposalContract.methods.voteAccept().encodeABI();

    // If a private key is given, sign the transaction,
    // otherwise, return the rawTx so that MEW can process it:
    if (!privateKey) {
        // Create transaction object:
        rawTx = {
            gasLimit: proposalCallGas,
            value: 0,
            to: proposalAddress,
            data: transactionData,
        };
        // we NEED an extra check, to be sure that rawTx.to is a valid ethereum address, otherwise the tx-funds will be lost:
        if (!rawTx.to || !isAddress(rawTx.to)) {
            throw new TreasuryException(errorMessages.INVALID_SMARTCONTRACT_ADDRESS);
        }

        // if the user connected to metamask, use metamask, otherwise return rawTx so that myEnergiWallet can process:
        if (await metamask.isConnected()) {
            rawTx.from = await metamask.getAddress();
            rawTx.gasLimit = web3.utils.toHex(proposalCallGas);
            rawTx.value = HEX0;
            tx = await metamask.sendTx(rawTx);
            // Metamask transaction: return tx as a String:
            return tx;
        }

        rawTx.warnings = 0; // suppress warnings from EnergiWallet
        rawTx.ts = Date.now(); // forcing non cachable url
        // Non Metamask transaction: return rawTx as an Object:

        return rawTx;
    }

    gasPrice = await web3.eth.getGasPrice();
    account = web3.eth.accounts.privateKeyToAccount(privateKey);
    txCount = await web3.eth.getTransactionCount(account.address, 'pending');
    // Create transaction object:
    rawTx = {
        nonce: toHex(txCount),
        gasLimit: toHex(proposalCallGas),
        gasPrice: toHex(gasPrice),
        to: proposalAddress,
        data: transactionData
    };

    energiCommon = await energiTx.getCommon(web3, networkAddress, networkId, chainId);
    tx = new Tx(rawTx, {common: energiCommon});

    privateKeyBuffer = Buffer.from(privateKey.substr(2), 'hex');
    tx.sign(privateKeyBuffer);

    // Broadcast the transaction to the testnet/mainnet
    serializedTx = tx.serialize();

    txResponse = await web3.eth.sendSignedTransaction('0x' + serializedTx.toString('hex'));

    if (!txResponse.status) {
        throw new TreasuryException(txResponse);
    }
    return txResponse;
};


/**
 * Lets us Masternode owner - the one who is going to sign - vote against a proposal.
 *
 * @method voteReject
 * @param uuid {String} unique proposal uuid to vote against
 * @param [privateKey] {String} only to be used by tests: the signer is the owner of the proposal
 * @return {Promise}
 * @since 0.4.0
 */
const voteReject = async (uuid, privateKey) => {
    let txCount, transactionData, rawTx, tx, account, gasPrice,
        serializedTx, privateKeyBuffer, txResponse, energiCommon,
        proposalContract, proposalAddress;

    if (!uuidUtils.isUuidv4(uuid, true)) { // second argument must be true: we also need to accept core node proposals which UUID's are less strict
        throw new TreasuryException(errorMessages.INVALID_UUID);
    }
    uuid = uuidUtils.uuidToHexBNString(uuid);

    proposalAddress = await treasuryContract.methods.uuid_proposal(uuid).call();
    if (!proposalAddress ||
        !web3.utils.isAddress(proposalAddress) ||
        (proposalAddress === '0x' + '0'.repeat(40))) {
        throw new TreasuryException(errorMessages.PROPOSAL_NOT_FOUND);
    }

    proposalContract = new web3.eth.Contract(abiProposal, proposalAddress);

    transactionData = await proposalContract.methods.voteReject().encodeABI();

    // If a private key is given, sign the transaction,
    // otherwise, return the rawTx so that MEW can process it:
    if (!privateKey) {
        // Create transaction object:
        rawTx = {
            gasLimit: proposalCallGas,
            value: 0,
            to: proposalAddress,
            data: transactionData,
        };
        // we NEED an extra check, to be sure that rawTx.to is a valid ethereum address, otherwise the tx-funds will be lost:
        if (!rawTx.to || !isAddress(rawTx.to)) {
            throw new TreasuryException(errorMessages.INVALID_SMARTCONTRACT_ADDRESS);
        }

        // if the user connected to metamask, use metamask, otherwise return rawTx so that myEnergiWallet can process:
        if (await metamask.isConnected()) {
            rawTx.from = await metamask.getAddress();
            rawTx.gasLimit = web3.utils.toHex(proposalCallGas);
            rawTx.value = HEX0;
            tx = await metamask.sendTx(rawTx);
            // Metamask transaction: return tx as a String:
            return tx;
        }

        rawTx.warnings = 0; // suppress warnings from EnergiWallet
        rawTx.ts = Date.now(); // forcing non cachable url
        // Non Metamask transaction: return rawTx as an Object:

        return rawTx;
    }

    gasPrice = await web3.eth.getGasPrice();
    account = web3.eth.accounts.privateKeyToAccount(privateKey);
    txCount = await web3.eth.getTransactionCount(account.address, 'pending');
    // Create transaction object:
    rawTx = {
        nonce: toHex(txCount),
        gasLimit: toHex('3000000'),
        gasPrice: toHex(gasPrice),
        to: proposalAddress,
        data: transactionData
    };

    energiCommon = await energiTx.getCommon(web3, networkAddress, networkId, chainId);
    tx = new Tx(rawTx, {common: energiCommon});

    privateKeyBuffer = Buffer.from(privateKey.substr(2), 'hex');
    tx.sign(privateKeyBuffer);

    // Broadcast the transaction to the testnet/mainnet
    serializedTx = tx.serialize();

    txResponse = await web3.eth.sendSignedTransaction('0x' + serializedTx.toString('hex'));

    if (!txResponse.status) {
        throw new TreasuryException(txResponse);
    }
    return txResponse;
};

/**
 * Generates an Exception thrown by a Treasury smartcontract error
 *
 * @method TreasuryException
 * @protected
 * @param txResponse {Object} the txResponse that raised the exception
 * @since 0.4.0
 */
const TreasuryException = function(txResponse) {
    // note: do not use arrow function, because we need to maintain the right context
    let keys, l, i, key;
    this.name = 'TreasuryException';
    if (typeof txResponse === 'string') {
        this.message = txResponse;
    }
    else {
        this.message = errorMessages.MASTERNODE_CONTRACT_EXCEPTION;
        keys = Object.keys(txResponse);
        l = keys.length;
        i = -1;
        while (++i < l) {
            key = keys[i];
            this[key] = txResponse[key];
        }
    }
};

module.exports = (web3Instance, options) => {
    if (options) {
        abiTreasury = options.abiTreasury || abiTreasury;
        networkAddress = options.networkAddress || networkAddress;
        smartContractAddressTreasury = options.smartContractAddressTreasury || smartContractAddressTreasury;
        networkId = options.networkId || networkId;
        chainId = options.chainId || chainId;
    }
    // check first if we have a valid web3 instance:
    if (web3Instance &&
        web3Instance.utils &&
        web3Instance.utils.unitMap &&
        (web3Instance.utils.unitMap.wei === '1')) {
        web3 = web3Instance;
        treasuryContract = new web3.eth.Contract(abiTreasury, smartContractAddressTreasury);
        return {
            budgetPropose,
            voteAccept,
            voteReject,
            treasuryContract
        };
    }
    console.error(errorMessages.INITIALIZE_WITH_WEB3);
};
